import { useState } from 'react';
import { useMutation } from 'react-apollo';
import axios from 'axios';

import spreadsheetUploadMutation from './spreadsheetUploadMutation';
import getConfig from '../../../../util/config';
import { getErrorString } from '../../../../util/normalize-errors';

/**
 * Handles uploading a spreadsheet (CSV or XLS/XLSX).
 *
 * @returns {{
 *  data: undefined|Array<Array>,
 *  onSpreadsheetUpload: Function,
 *  loading: boolean,
 *  error: string
 * }}
 */
const useSpreadsheetUpload = () => {
  const config = getConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState();
  const [spreadsheetUpload] = useMutation(spreadsheetUploadMutation);

  const onSpreadsheetUpload = async file => {
    if (loading) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get signed URL.
      const response = await spreadsheetUpload({
        variables: {
          input: {
            filename: file.name,
            content_type: file.type,
          },
        },
      });

      // Upload file to S3
      const uploadData = response.data.spreadsheetUpload;
      await axios.put(uploadData.upload_url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      // Get the parsed data
      const parse = await axios.get(
        `${config.graphql.clientBaseUrl}/spreadsheet/parse/${uploadData.file_id}`,
        {
          withCredentials: true,
        },
      );

      setLoading(false);
      setData(parse.data.data.filter(row => row.length > 0));
    } catch (responseError) {
      setError(getErrorString(responseError));
      setLoading(false);
    }
  };

  return {
    onSpreadsheetUpload,
    loading,
    data,
    error,
  };
};

export default useSpreadsheetUpload;
